// The back-office menu as data: the worlds, per world its subjects, and per subject the
// screens — first what waits to be done, then the lists — with the world's own setup
// apart. The world bar, its drop-down menu, the title line, the tab title and the
// Dagstart all read this file, so a screen is added in one place and can never end up
// in two worlds at once.
//
// Navigation round 2 (docs/navigatie-design-briefing-ronde2.md, N16): a world opens a
// menu with all its screens; there is no second row of tabs, and a screen with tabs of
// its own that are in the menu (Orders) lost them. One world per kind of work: the day,
// the freight, the building, the relationship, the money, running the place.
import type { ModuleKey, SessionPrincipal } from "@/lib/auth";
import type { nl } from "@/i18n/locales/nl";

export type NavLabelKey = keyof typeof nl;

export type WorldId = "today" | "transport" | "warehouse" | "organizations" | "commerce" | "admin";

export type NavIconName =
  | "sun"
  | "key"
  | "board"
  | "truck"
  | "party"
  | "reservations"
  | "invoice"
  | "gear"
  | "home"
  | "mail"
  | "calendar"
  | "tasks"
  | "clipboard"
  | "activity"
  | "target"
  | "trailer"
  | "idCard"
  | "mapPin"
  | "dossier"
  | "document"
  | "worklist"
  | "users"
  | "org"
  | "warehouse"
  | "shield"
  | "plug"
  | "requirements"
  | "tag"
  | "receipt"
  | "fileX";

/** The colour of a subject's icon (Camiel's sketch, 29-10-2026): a --subject-* token of @wlx/tokens. */
export type NavTone = "blue" | "green" | "orange" | "teal";

/** A count the menu and the Dagstart show beside a work queue; GET /nav/counts answers them (one database count each). */
export type NavCountKey =
  | "orderIntake"
  | "ordersBlocked"
  | "ordersPrices"
  | "documentCheck"
  | "palletCheck"
  | "missingCmrs"
  | "partiesApproval"
  | "tasksForYou";

type SearchLike = Record<string, unknown>;

export interface NavItem {
  /** Where the item links to — for a placeholder, the route it is going to get. */
  href: string;
  labelKey: NavLabelKey;
  /** Work that waits on someone (with a count), or a list you look things up in. */
  kind: "work" | "list";
  /**
   * The search the link opens with, for items that share a route (the work queues of
   * Orders). Without it the link opens the screen as you left it (lib/listState).
   */
  search?: Record<string, string>;
  /** Which search on the shared route is this item; only with `search`. */
  when?: (search: SearchLike) => boolean;
  /** The module in the Rechten matrix that unlocks the item. */
  moduleKey?: ModuleKey;
  /** Another module that opens the same screen (mail: only your own mailbox), or several. */
  altModuleKey?: ModuleKey | readonly ModuleKey[];
  /** Screens that are hardcoded admin-only instead of being a module. */
  adminOnly?: boolean;
  /** In the menu before the screen exists: drawn, never linked, never a destination. */
  placeholder?: boolean;
  /** Screens that are not a menu item of their own but belong to this one. */
  subRoutes?: readonly { href: string; labelKey: NavLabelKey }[];
  /** The count beside it, when it is a work queue that has one. */
  count?: NavCountKey;
  /** Its icon as a favourite in the world bar; without one, the icon of its subject. */
  icon?: NavIconName;
}

export interface NavGroup {
  id: string;
  labelKey: NavLabelKey;
  /** Beside the subject's name in the world's menu, and on its screens as a favourite. */
  icon: NavIconName;
  /** Its icon's colour, in the menu and on the Dagstart. The subjects of one world each have their own. */
  tone: NavTone;
  items: readonly NavItem[];
}

export interface NavWorld {
  id: WorldId;
  labelKey: NavLabelKey;
  icon: NavIconName;
  /** Set apart from the work (Beheer): last in the world bar. */
  atFoot?: boolean;
  groups: readonly NavGroup[];
  /** Setting things up for this world (N2): the right-hand column of its menu. */
  setup?: readonly NavItem[];
}

// Orders' work queues share one route; the status in the search says which.
const ORDER_LIST_STATUSES = ["all", "accepted", "rejected", "cancelled"];
const ordersStatus = (search: SearchLike) => (typeof search.status === "string" ? search.status : "");
const ORDERS_READ = { moduleKey: "tms_orders", altModuleKey: "tms_orders_write" } as const;
const PLANNING_READ = { moduleKey: "tms_planning", altModuleKey: "tms_planning_write" } as const;
const BILLING_READ = { moduleKey: "billing", altModuleKey: "billing_write" } as const;
const PURCHASE_READ = { moduleKey: "purchase_invoices", altModuleKey: "purchase_invoices_write" } as const;
const WMS_MASTERDATA = { moduleKey: "wms_masterdata", altModuleKey: "wms_masterdata_write" } as const;

export const NAV_WORLDS: readonly NavWorld[] = [
  {
    // Everything that revolves around you today. Everyone, several times an hour; the
    // Dagstart gathers the work queues of every world (N7).
    id: "today",
    labelKey: "nav.world.today",
    icon: "sun",
    groups: [
      {
        id: "today",
        labelKey: "nav.world.today",
        icon: "sun",
        tone: "teal",
        items: [
          { href: "/dashboard", labelKey: "nav.dashboard", kind: "list", moduleKey: "dashboard", icon: "home" },
          // The mail screen also opens for pilot members who may only link their OWN mailbox.
          { href: "/mail", labelKey: "nav.mail", kind: "list", moduleKey: "mail", altModuleKey: "mail_personal", icon: "mail" },
          { href: "/calendar", labelKey: "nav.calendar", kind: "list", moduleKey: "calendar", icon: "calendar" },
          {
            href: "/tasks",
            labelKey: "nav.tasks",
            kind: "list",
            icon: "tasks",
            moduleKey: "tasks",
            count: "tasksForYou",
            subRoutes: [{ href: "/tasks/recurring", labelKey: "tasks.recPageTitle" }],
          },
          { href: "/reservations", labelKey: "nav.reservations", kind: "list", moduleKey: "reservations", icon: "reservations" },
        ],
      },
    ],
    setup: [
      { href: "/mail/mailboxes", labelKey: "nav.mailboxes", kind: "list", adminOnly: true },
      // Inrichting of Mail (N14).
      { href: "/focus-areas", labelKey: "nav.focusAreas", kind: "list", adminOnly: true },
      // Everyone with Taken opens it from the button in Taken; in the menu only admins.
      { href: "/tasks/categories", labelKey: "nav.taskCategories", kind: "list", adminOnly: true },
      { href: "/reservations/resources", labelKey: "nav.resources", kind: "list", adminOnly: true },
    ],
  },
  {
    // From transport order to delivered trip, with the paperwork that travels with the
    // freight, and what it is planned with (Resources was a world of its own until N16).
    id: "transport",
    labelKey: "nav.world.transport",
    icon: "board",
    groups: [
      {
        // Invoer samen (N6): everything that comes in and is entered, in one place.
        id: "orders",
        labelKey: "nav.group.orders",
        icon: "clipboard",
        tone: "blue",
        items: [
          // What order entry and planning still have to enter, also as a window of its own (docs/invoerbak.md).
          { href: "/intake", labelKey: "nav.orderIntake", kind: "work", moduleKey: "order_intake", count: "orderIntake" },
          {
            href: "/orders",
            labelKey: "nav.ordersBlocked",
            kind: "work",
            ...ORDERS_READ,
            count: "ordersBlocked",
            search: {},
            when: (search) => ordersStatus(search) === "",
          },
          {
            href: "/orders",
            labelKey: "nav.ordersPrenotify",
            kind: "work",
            ...ORDERS_READ,
            search: { status: "prenotify" },
            when: (search) => ordersStatus(search) === "prenotify",
          },
          {
            href: "/orders",
            labelKey: "nav.ordersPrices",
            kind: "work",
            ...ORDERS_READ,
            count: "ordersPrices",
            search: { status: "prices" },
            when: (search) => ordersStatus(search) === "prices",
          },
          // Hager: what is not scanned in on the day before its unloading (docs/tms-orderintake.md §3j).
          { href: "/carry-over", labelKey: "nav.carryOver", kind: "work", moduleKey: "hager_carry_over" },
          {
            href: "/orders",
            labelKey: "nav.transportOrders",
            kind: "list",
            ...ORDERS_READ,
            search: { status: "all" },
            when: (search) => ORDER_LIST_STATUSES.includes(ordersStatus(search)),
          },
          { href: "/warehouse-orders", labelKey: "nav.warehouseOrders", kind: "list", ...ORDERS_READ },
          { href: "/standing-orders", labelKey: "nav.standingOrders", kind: "list", ...ORDERS_READ },
          {
            href: "/orders",
            labelKey: "nav.ordersDrafts",
            kind: "list",
            ...ORDERS_READ,
            search: { status: "drafts" },
            when: (search) => ordersStatus(search) === "drafts",
          },
        ],
      },
      {
        id: "planning",
        labelKey: "nav.group.planning",
        icon: "board",
        tone: "green",
        items: [
          { href: "/planning", labelKey: "nav.planning", kind: "work", ...PLANNING_READ },
          // The running trips (Lucien, 22-10-2026, P59).
          { href: "/tracking", labelKey: "nav.tracking", kind: "work", ...PLANNING_READ, icon: "activity" },
          { href: "/exceptions", labelKey: "nav.exceptions", kind: "work", moduleKey: "tms_exceptions", placeholder: true },
          { href: "/trips", labelKey: "nav.trips", kind: "list", ...PLANNING_READ, icon: "truck" },
        ],
      },
      {
        // The paper that travels with the freight and the pallets on it (N8).
        id: "documents",
        labelKey: "nav.group.documents",
        icon: "document",
        tone: "orange",
        items: [
          // First the scans linked, then the pallets of the signed CMRs one by one (docs/scantafel.md §10); the scans
          // without a consignment are only Documentcontrole's queue since 03-11-2026 (§13).
          { href: "/document-check", labelKey: "nav.documentCheck", kind: "work", ...ORDERS_READ, count: "documentCheck" },
          { href: "/pallet-check", labelKey: "nav.palletCheck", kind: "work", ...ORDERS_READ, count: "palletCheck" },
          // The other way round: unloaded, and the signed CMR not in yet (§13, was TaskCentre task 33).
          { href: "/missing-cmrs", labelKey: "nav.missingCmrs", kind: "work", ...ORDERS_READ, count: "missingCmrs", icon: "fileX" },
          { href: "/dossiers", labelKey: "nav.dossiers", kind: "list", moduleKey: "dossiers", icon: "dossier" },
          { href: "/documents", labelKey: "nav.allDocuments", kind: "list", moduleKey: "documents", search: { state: "linked" } },
          // The pallet account per relation (docs/scantafel.md §5).
          {
            href: "/pallet-accounts",
            labelKey: "nav.palletAccounts",
            kind: "list",
            moduleKey: "tms_orders",
            altModuleKey: ["tms_orders_write", "pallet_accounts_write"],
          },
        ],
      },
      {
        // What you plan with: vehicles and trailers in one list (S31), and the people who drive them.
        id: "resources",
        labelKey: "nav.group.resources",
        icon: "truck",
        tone: "teal",
        items: [
          {
            href: "/transport-units",
            labelKey: "nav.transportUnits",
            kind: "list",
            icon: "trailer",
            moduleKey: "tms_transport_units",
            altModuleKey: "tms_resources_write",
            subRoutes: [{ href: "/transport-units/rdw", labelKey: "resources.rdwCompare" }],
          },
          // Only its own module opens it: the reason by an unavailability may be private.
          { href: "/drivers", labelKey: "nav.drivers", kind: "list", moduleKey: "tms_drivers", icon: "idCard" },
        ],
      },
    ],
    setup: [
      // Every ordermail of every customer, and which mailboxes feed the orderintake (docs/invoerbak.md §3b).
      { href: "/order-mails", labelKey: "nav.orderMails", kind: "list", adminOnly: true },
      // What a consignment asks of the transport (S47).
      { href: "/consignment-requirements", labelKey: "nav.consignmentRequirements", kind: "list", adminOnly: true },
      { href: "/adr-codes", labelKey: "nav.adrCodes", kind: "list", adminOnly: true },
      // The planners read it, only an admin changes it (P31).
      { href: "/pendel-routes", labelKey: "nav.pendelRoutes", kind: "list", moduleKey: "tms_planning_write", altModuleKey: "tms_planning" },
    ],
  },
  {
    // The warehouse: the WMS that replaces Scoretrace. Its own Vandaag went into the Dagstart (N13).
    id: "warehouse",
    labelKey: "nav.world.warehouse",
    icon: "warehouse",
    groups: [
      {
        id: "execution",
        labelKey: "nav.group.execution",
        icon: "warehouse",
        tone: "blue",
        items: [
          { href: "/warehouse/work", labelKey: "nav.warehouseWork", kind: "work", moduleKey: "wms_stock", altModuleKey: "wms_outbound" },
          { href: "/warehouse/outbound", labelKey: "nav.warehouseOutbound", kind: "work", moduleKey: "wms_outbound", altModuleKey: "wms_outbound_write" },
          { href: "/warehouse/inbound", labelKey: "nav.warehouseInbound", kind: "work", moduleKey: "wms_inbound", altModuleKey: "wms_inbound_write" },
          // What a depot unloads and loads on a day (docs/tms-crossdock.md); the planning sees it too (CD4).
          {
            href: "/warehouse/crossdock",
            labelKey: "nav.warehouseCrossdock",
            kind: "work",
            moduleKey: "crossdock",
            altModuleKey: ["crossdock_write", "tms_planning", "tms_planning_write"],
          },
          // The inbound and outbound confirmations: the back-office approves a deviation.
          {
            href: "/warehouse/confirmations",
            labelKey: "nav.warehouseConfirmations",
            kind: "work",
            moduleKey: "wms_inbound",
            altModuleKey: ["wms_outbound", "tms_orders", "wms_inbound_write", "wms_outbound_write", "tms_orders_write"],
          },
        ],
      },
      {
        id: "stock",
        labelKey: "nav.group.stock",
        icon: "worklist",
        tone: "green",
        items: [
          {
            href: "/warehouse/replenishment",
            labelKey: "nav.warehouseReplenishment",
            kind: "work",
            moduleKey: "wms_stock",
            altModuleKey: "wms_stock_write",
          },
          { href: "/warehouse/stock", labelKey: "nav.warehouseStock", kind: "list", moduleKey: "wms_stock", altModuleKey: "wms_stock_write" },
          { href: "/worklists", labelKey: "nav.worklists", kind: "list", moduleKey: "worklists", icon: "worklist" },
        ],
      },
    ],
    setup: [
      { href: "/warehouse/locations", labelKey: "nav.warehouseLocations", kind: "list", ...WMS_MASTERDATA },
      { href: "/warehouse/articles", labelKey: "nav.warehouseArticles", kind: "list", ...WMS_MASTERDATA },
      { href: "/warehouse/vas", labelKey: "nav.warehouseVas", kind: "list", ...WMS_MASTERDATA },
      { href: "/warehouse/settings", labelKey: "nav.warehouseSettings", kind: "list", ...WMS_MASTERDATA },
      // The scanner logins of the floor without Entra: made, reset and unlocked by the team leader.
      { href: "/warehouse/scanner-users", labelKey: "nav.warehouseScannerUsers", kind: "list", moduleKey: "wms_scanner_logins" },
    ],
  },
  {
    // Who you do business with — our own organisation among them (S27) — and where you
    // drive to. Customer and charter are roles of one relation (docs/tms-stamdata.md §5).
    // The customers' move to the portal is done (N18): Portaal is a tab on the relation only.
    id: "organizations",
    labelKey: "nav.world.organizations",
    icon: "party",
    groups: [
      {
        id: "relations",
        labelKey: "nav.group.relations",
        icon: "party",
        tone: "blue",
        items: [{ href: "/parties", labelKey: "nav.parties", kind: "list", moduleKey: "parties", count: "partiesApproval" }],
      },
      {
        id: "locations",
        labelKey: "nav.group.locations",
        icon: "mapPin",
        tone: "green",
        items: [
          {
            href: "/locations",
            labelKey: "nav.locations",
            kind: "list",
            moduleKey: "tms_locations",
            // The addresses with more than one location, to join what is one place (23-09-2026).
            subRoutes: [{ href: "/locations/duplicates", labelKey: "locations.duplicatesTitle" }],
          },
        ],
      },
    ],
    setup: [
      // Rijkevoort, Haps, Waalwijk, Veghel: where vehicles and drivers are based.
      { href: "/branches", labelKey: "nav.branches", kind: "list", adminOnly: true },
    ],
  },
  {
    // The money: what we invoice and what we are invoiced (Finance was a world of its own until N16).
    id: "commerce",
    labelKey: "nav.world.commerce",
    icon: "invoice",
    groups: [
      {
        id: "invoicing",
        labelKey: "nav.group.invoicing",
        icon: "invoice",
        tone: "blue",
        items: [
          // The invoice run (docs/tms-facturatie.md §18).
          { href: "/billing/invoices", labelKey: "nav.invoices", kind: "work", ...BILLING_READ },
          // The weeks the billing team types the quantities in (docs/tms-facturatie-warehouse.md).
          {
            href: "/billing/warehouse",
            labelKey: "nav.warehouseBilling",
            kind: "work",
            moduleKey: "billing",
            altModuleKey: ["billing_write", "price_agreements_approve"],
          },
        ],
      },
      {
        id: "prices",
        labelKey: "nav.group.prices",
        icon: "tag",
        tone: "green",
        items: [
          {
            href: "/billing/tariffs",
            labelKey: "nav.tariffs",
            kind: "list",
            moduleKey: "billing",
            altModuleKey: ["billing_write", "price_agreements_write", "price_agreements_approve"],
          },
          { href: "/billing/diesel", labelKey: "nav.diesel", kind: "list", ...BILLING_READ },
        ],
      },
      {
        // The invoices our suppliers send, coded in WLX and approved in AFAS (docs/inkoopfacturen.md).
        id: "purchasing",
        labelKey: "nav.group.purchasing",
        icon: "receipt",
        tone: "orange",
        items: [{ href: "/purchase-invoices", labelKey: "nav.purchaseInvoices", kind: "work", ...PURCHASE_READ }],
      },
    ],
    setup: [
      // "Lezen klopt niet": what was reported, to improve the reading (§5.5).
      { href: "/purchase-invoices/read-feedback", labelKey: "nav.readFeedback", kind: "list", ...PURCHASE_READ },
      // BP's products and the account each is booked on (§17).
      { href: "/purchase-invoices/fuel-card-products", labelKey: "nav.fuelCardProducts", kind: "list", ...PURCHASE_READ },
      // What WLX learned from changes by hand on the coding (§7.8).
      { href: "/purchase-invoices/coding-rules", labelKey: "nav.codingRules", kind: "list", ...PURCHASE_READ },
    ],
  },
  {
    // Running the system, not doing the work (N2). Requirements is the one module here (N14).
    id: "admin",
    labelKey: "nav.world.admin",
    icon: "gear",
    atFoot: true,
    groups: [
      {
        id: "system",
        labelKey: "nav.group.system",
        icon: "shield",
        tone: "blue",
        items: [
          { href: "/users", labelKey: "nav.users", kind: "list", adminOnly: true, icon: "users" },
          { href: "/permissions", labelKey: "nav.permissions", kind: "list", adminOnly: true, icon: "shield" },
          // What DataBridge sent WLX and what became of it.
          { href: "/integrations", labelKey: "nav.integrations", kind: "list", adminOnly: true, icon: "plug" },
          // How fast the API answers per route (26-09-2026).
          { href: "/performance", labelKey: "nav.performance", kind: "list", adminOnly: true },
          // The secrets this environment leans on and when they expire (docs/geheimen.md).
          { href: "/secrets", labelKey: "nav.secrets", kind: "list", adminOnly: true, icon: "key" },
        ],
      },
      {
        id: "project",
        labelKey: "nav.group.project",
        icon: "requirements",
        tone: "green",
        items: [{ href: "/requirements", labelKey: "nav.requirements", kind: "list", moduleKey: "requirements" }],
      },
    ],
  },
];

type NavSession = Pick<SessionPrincipal, "roles" | "modules"> | null | undefined;

function holds(session: NavSession, key: ModuleKey | readonly ModuleKey[] | undefined): boolean {
  if (key === undefined) return false;
  return (typeof key === "string" ? [key] : key).some((k) => !!session?.modules?.includes(k));
}

// What gets drawn, not what is allowed: the API enforces the matrix on every route regardless.
export function isItemVisible(item: NavItem, session: NavSession): boolean {
  // A placeholder is structure, not access.
  if (item.placeholder) return true;
  if (item.adminOnly) return !!session?.roles?.includes("admin");
  if (!item.moduleKey) return true;
  return holds(session, item.moduleKey) || holds(session, item.altModuleKey);
}

export interface VisibleGroup {
  group: NavGroup;
  items: readonly NavItem[];
}

export interface VisibleWorld {
  world: NavWorld;
  groups: readonly VisibleGroup[];
  setup: readonly NavItem[];
}

/** Every item of a world this person sees, the setup last. */
export function itemsOf(entry: VisibleWorld): NavItem[] {
  return [...entry.groups.flatMap((g) => g.items), ...entry.setup];
}

// The worlds this person sees, in menu order. A subject whose screens all fall away is left
// off — a placeholder alone does not keep it — and so is a world with nothing left in it.
export function visibleWorlds(session: NavSession): VisibleWorld[] {
  return NAV_WORLDS.map((world) => ({
    world,
    groups: world.groups
      .map((group) => ({ group, items: group.items.filter((item) => isItemVisible(item, session)) }))
      .filter((entry) => entry.items.some((item) => !item.placeholder)),
    setup: (world.setup ?? []).filter((item) => isItemVisible(item, session)),
  })).filter((entry) => entry.groups.length > 0 || entry.setup.length > 0);
}

// The first screen of a world that is a real screen. Undefined for a world of placeholders only.
export function firstScreen(entry: VisibleWorld): NavItem | undefined {
  return itemsOf(entry).find((item) => !item.placeholder);
}

export interface NavRoute {
  href: string;
  labelKey: NavLabelKey;
  worldId: WorldId;
}

// Every route the menu knows. Where items share a route (the work queues of Orders) the
// route is named after their subject: a card opened from any of them goes back to "Orders".
function routesOf(worlds: readonly NavWorld[]): NavRoute[] {
  const routes: NavRoute[] = [];
  const seen = new Set<string>();
  for (const world of worlds) {
    const pairs: [NavItem, NavLabelKey][] = [
      ...world.groups.flatMap((group) => group.items.map((item) => [item, group.labelKey] as [NavItem, NavLabelKey])),
      ...(world.setup ?? []).map((item) => [item, item.labelKey] as [NavItem, NavLabelKey]),
    ];
    for (const [item, groupLabel] of pairs) {
      if (item.placeholder || seen.has(item.href)) continue;
      seen.add(item.href);
      const shared = pairs.filter(([other]) => other.href === item.href).length > 1;
      routes.push({ href: item.href, labelKey: shared ? groupLabel : item.labelKey, worldId: world.id });
      for (const sub of item.subRoutes ?? []) routes.push({ href: sub.href, labelKey: sub.labelKey, worldId: world.id });
    }
  }
  return routes;
}

// Every screen the menu knows, whoever is looking.
export const NAV_ROUTES: readonly NavRoute[] = routesOf(NAV_WORLDS);

// The route a pathname belongs to: the exact one, else the longest route it sits under.
// Longest rather than first, so /mail/mailboxes can never be swallowed by /mail.
export function matchRoute<R extends { href: string }>(pathname: string, routes: readonly R[]): R | undefined {
  const exact = routes.find((r) => r.href === pathname);
  if (exact) return exact;
  let best: R | undefined;
  for (const r of routes) {
    if (pathname.startsWith(r.href + "/") && (!best || r.href.length > best.href.length)) {
      best = r;
    }
  }
  return best;
}

export interface NavPlace {
  world: NavWorld;
  /** Undefined for a setup screen. */
  group: NavGroup | undefined;
  /** The item you are on; undefined on a card that sits under a route several items share. */
  item: NavItem | undefined;
  /** On the screen of the item itself, not on a card or a sub-screen under it. */
  exact: boolean;
  /** On a sub-screen of the item (/tasks/recurring): its own label. */
  subLabelKey?: NavLabelKey;
}

interface Candidate {
  world: NavWorld;
  group: NavGroup | undefined;
  item: NavItem;
}

function candidates(worlds: readonly NavWorld[]): Candidate[] {
  return worlds.flatMap((world) => [
    ...world.groups.flatMap((group) => group.items.map((item) => ({ world, group, item }))),
    ...(world.setup ?? []).map((item) => ({ world, group: undefined, item })),
  ]);
}

const ALL_CANDIDATES = candidates(NAV_WORLDS);

// Where a URL is in the menu: the world, the subject and the item. Items that share a
// route are told apart by their `when`; a card under a shared route has the subject only.
export function placeOf(pathname: string, search: SearchLike, pool: readonly Candidate[] = ALL_CANDIDATES): NavPlace | undefined {
  const live = pool.filter((c) => !c.item.placeholder);
  const exactHits = live.filter((c) => c.item.href === pathname);
  if (exactHits.length > 0) {
    const hit = exactHits.length === 1 ? exactHits[0] : (exactHits.find((c) => c.item.when?.(search)) ?? exactHits[0]);
    return { world: hit.world, group: hit.group, item: hit.item, exact: true };
  }
  for (const c of live) {
    const sub = c.item.subRoutes?.find((s) => s.href === pathname);
    if (sub) return { world: c.world, group: c.group, item: c.item, exact: false, subLabelKey: sub.labelKey };
  }
  // A card or a screen under an item: the longest route it sits under.
  const route = matchRoute(pathname, live.map((c) => ({ href: c.item.href, c })));
  if (!route) return undefined;
  const shared = live.filter((c) => c.item.href === route.href);
  const only = shared.length === 1 ? shared[0].item : undefined;
  return { world: route.c.world, group: route.c.group, item: only, exact: false };
}

export interface NavState {
  /** The worlds this person sees. */
  worlds: VisibleWorld[];
  /** Where the page is in the menu, whoever is looking. */
  place: NavPlace | undefined;
  /** The world the page belongs to for this person. */
  worldId: WorldId | undefined;
}

// Where you are, from the URL alone and never from component state — a deep link to
// /dossiers has to open Transport. The world prefers one this person can see:
// /tasks/categories sits in Vandaag's setup, which only admins see in the menu, but
// anyone with Taken reaches it through the button in Taken.
export function resolveNav(pathname: string, search: SearchLike, session: NavSession): NavState {
  const worlds = visibleWorlds(session);
  const place = placeOf(pathname, search);
  const own = placeOf(
    pathname,
    search,
    candidates(worlds.map((entry) => ({ ...entry.world, groups: entry.groups.map((g) => ({ ...g.group, items: g.items })), setup: entry.setup }))),
  );
  return { worlds, place, worldId: own?.world.id ?? place?.world.id };
}

export function worldById(id: WorldId | undefined): NavWorld | undefined {
  return NAV_WORLDS.find((world) => world.id === id);
}

// The key of a menu item: its route, with the search of a work queue that shares one
// ("/orders?status=prices"). What a favourite is stored as (GET /nav/favorites).
export function itemKey(item: NavItem): string {
  const query = new URLSearchParams(item.search ?? {}).toString();
  return query ? `${item.href}?${query}` : item.href;
}

/** The most favourites someone keeps in the world bar; the API holds to the same. */
export const MAX_FAVORITES = 12;

// The favourites of someone who has not picked their own: the first five of these they can
// open (Camiel's sketch of 29-10-2026: Mail, Planbord, Orders, Klanten).
const DEFAULT_FAVORITES = ["/mail", "/planning", "/orders?status=all", "/parties", "/tasks", "/warehouse/outbound", "/warehouse/stock"];
const DEFAULT_COUNT = 5;

export interface NavFavorite {
  key: string;
  item: NavItem;
  /** The item's own icon, else its subject's, else its world's. */
  icon: NavIconName;
}

// Every screen this person can open, as it would stand in the world bar.
function favoriteMap(worlds: readonly VisibleWorld[]): Map<string, NavFavorite> {
  const byKey = new Map<string, NavFavorite>();
  for (const entry of worlds) {
    for (const { group, items } of entry.groups) {
      for (const item of items) {
        if (!item.placeholder) byKey.set(itemKey(item), { key: itemKey(item), item, icon: item.icon ?? group.icon });
      }
    }
    for (const item of entry.setup) byKey.set(itemKey(item), { key: itemKey(item), item, icon: item.icon ?? "gear" });
  }
  return byKey;
}

// The favourites drawn in the world bar, in their order: the stored keys, or the default
// ones when nothing is stored. A key this person cannot open (any more) is kept in the
// store but not drawn; a placeholder never is a favourite.
export function favoritesOf(stored: readonly string[] | null | undefined, worlds: readonly VisibleWorld[]): NavFavorite[] {
  const byKey = favoriteMap(worlds);
  const keys = stored ?? DEFAULT_FAVORITES;
  const drawn = keys.map((key) => byKey.get(key)).filter((fav): fav is NavFavorite => fav !== undefined);
  return stored ? drawn : drawn.slice(0, DEFAULT_COUNT);
}

// The screen you are on as the world bar names it (Camiel, 29-10-2026: row 2 only for a
// screen with tabs, so row 1 says where you are): the menu item of the page, also on a card
// under it, when this person can open it. Undefined on a card under a route several screens
// share (an order: Orders), and on a page the menu does not know.
export function hereOf(place: NavPlace | undefined, worlds: readonly VisibleWorld[]): NavFavorite | undefined {
  return place?.item ? favoriteMap(worlds).get(itemKey(place.item)) : undefined;
}
